import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Course {
  title: string;
  description: string;
  image: string;
  link: string;
}

const truncate = (text: string, max = 110) =>
  text.length > max ? text.slice(0, max) + "..." : text;

const CourseCard = ({ title, description, image, link }: Course) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-md border border-gray-100 shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col font-sans">
      <Link to={link} className="relative overflow-hidden group">
        <div className="w-full bg-gray-100 flex items-center justify-center" style={{ minHeight: 220 }}>
          <img
            src={image}
            alt={title}
            className="w-full h-auto max-h-72 object-contain transition-transform duration-300 group-hover:scale-105"
            style={{ background: "transparent" }}
          />
        </div>
      </Link>
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-semibold text-center mt-2 transition-colors duration-200 text-[#009fe3] hover:text-[#007cb5]">
          <Link to={link}>{title}</Link>
        </h3>
        <div className="w-10 h-1 bg-[#009fe3] rounded-full mx-auto my-2"></div>
        <p className="text-gray-700 text-base text-justify flex-grow mb-6">{truncate(description)}</p>
        <Link
          to={link}
          className="mt-auto block w-full text-center bg-[#009fe3] hover:bg-[#007cb5] text-white font-semibold text-base py-2 px-4 rounded-lg shadow transition"
        >
          {t("courses.findDetails")}
        </Link>
      </div>
    </div>
  );
};

const Feature = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);

  useEffect(() => {
    setCourses([
      {
        title: t("courses.ausbildung.title"),
        description: t("courses.ausbildung.description"),
        image: "assets/img/course/ausbildung-prep-course-home.png",
        link: "/course-details/ausbildung-preparatory-course",
      },
      {
        title: t("courses.german.title"),
        description: t("courses.german.description"),
        image: "assets/img/course/german-language-course-home.png",
        link: "/course-details/german-language-course",
      },
      {
        title: t("courses.fsj.title"),
        description: t("courses.fsj.description"),
        image: "assets/img/course/fsj_bfd_preparatory_course.png",
        link: "/course-details/fsj-bfd-preparatory-course",
      },
    ]);
  }, [t]);

  return (
    <section className="max-w-7xl mx-auto px-2 sm:px-4 py-16 font-sans">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-extrabold text-[#009fe3] tracking-tight mb-2">
          {t("courses.heading")}
        </h2>
        <div className="mx-auto w-16 h-1 bg-[#009fe3] rounded-full mb-4"></div>
        <p className="text-gray-700 text-base max-w-2xl mx-auto leading-relaxed">
          {t("courses.subheading")}
        </p>
      </div>

      <div className="grid gap-10 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {courses.map((course, idx) => (
          <CourseCard key={idx} {...course} />
        ))}
      </div>
    </section>
  );
};

export default Feature;
